class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // Remove the cart icon accessibility setup that was preventing default behavior
    // this.setHeaderCartIconAccessibility();
  }

  // REMOVED: setHeaderCartIconAccessibility() method
  // This was preventing the cart icon from navigating to the cart page
  // The cart icon should now work as a normal link to /cart

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    if (parsedState.item_count > 0) {
      this.classList.remove('is-empty');
    }

    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-icon-bubble') return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems?.updateDrawerCartBubble) {
      if (parsedState.item_count != null) {
        cartDrawerItems.updateDrawerCartBubble(parsedState);
      } else {
        fetch('/cart.js')
          .then((response) => response.json())
          .then((cart) => {
            cartDrawerItems.updateDrawerCartBubble({
              ...parsedState,
              item_count: cart.item_count,
              items: cart.items,
            });
          })
          .catch(() => {
            cartDrawerItems.updateDrawerCartBubble(parsedState);
          });
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateDrawerCartBubble(parsedState) {
    if (typeof window.rushFeeUpdateCartBubbleCount === 'function') {
      window.rushFeeUpdateCartBubbleCount(parsedState);
      return;
    }

    this.updateCartIconBubble(parsedState);
  }

  getDrawerScrollContainer() {
    return this.closest('.drawer__inner') || this;
  }

  renderUpdatedSections(parsedState) {
    const sectionHtml = parsedState.sections['cart-drawer'];
    if (!sectionHtml) return;

    const scrollContainer = this.getDrawerScrollContainer();
    const scrollTop = scrollContainer.scrollTop;

    if (parsedState.item_count === 0) {
      const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
      if (drawerInner) {
        drawerInner.innerHTML = this.getSectionInnerHTML(sectionHtml, '.drawer__inner');
      }

      if (typeof window.rushFeeSyncEmptyState === 'function') {
        window.rushFeeSyncEmptyState(0);
      }

      this.updateDrawerCartBubble(parsedState);
      return;
    }

    const contents = this.querySelector('.js-contents');
    const newContentsHtml = this.getSectionInnerHTML(sectionHtml, '#CartDrawer-CartItems');
    if (contents && newContentsHtml) {
      contents.innerHTML = newContentsHtml;
    }

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    this.updateCartDrawerFooter(doc);
    this.updateCartDrawerProgress(sectionHtml);
    scrollContainer.scrollTop = scrollTop;

    this.updateDrawerCartBubble(parsedState);
  }

  replaceCartDrawerContents(sectionHtml) {
    const scrollContainer = this.getDrawerScrollContainer();
    const scrollTop = scrollContainer.scrollTop;
    this.classList.add('is-updating');

    if (!sectionHtml) {
      this.classList.remove('is-updating');
      return;
    }

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const lineItemCount = doc.querySelectorAll('cart-drawer-items .cart-item').length;
    const cartDrawer = document.querySelector('cart-drawer');

    if (lineItemCount === 0) {
      const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
      const newInnerHtml = this.getSectionInnerHTML(sectionHtml, '.drawer__inner');
      if (drawerInner && newInnerHtml) {
        drawerInner.innerHTML = newInnerHtml;
      }
      if (cartDrawer) cartDrawer.classList.add('is-empty');
      this.classList.add('is-empty');

      if (typeof window.rushFeeSyncEmptyState === 'function') {
        window.rushFeeSyncEmptyState(0);
      }
    } else {
      if (cartDrawer) cartDrawer.classList.remove('is-empty');
      this.classList.remove('is-empty');
      document.querySelector('#CartDrawer .drawer__inner-empty')?.remove();

      const contents = this.querySelector('.js-contents');
      const newContentsHtml = this.getSectionInnerHTML(sectionHtml, '#CartDrawer-CartItems');
      if (contents && newContentsHtml) {
        contents.innerHTML = newContentsHtml;
      }

      this.updateCartDrawerFooter(doc);
      this.updateCartDrawerProgress(sectionHtml);
      scrollContainer.scrollTop = scrollTop;
    }

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        this.updateDrawerCartBubble({
          item_count: cart.item_count,
          items: cart.items,
          sections: {},
        });
      })
      .catch(() => {});

    requestAnimationFrame(() => this.classList.remove('is-updating'));
  }

  replaceCartDrawerTbody(doc) {
    const contents = this.querySelector('.js-contents');
    const newContents = doc.querySelector('#CartDrawer-CartItems');
    if (contents && newContents) {
      contents.innerHTML = newContents.innerHTML;
    }
  }

  syncFooterNode(currentParent, newParent, selector) {
    const newNode = newParent.querySelector(selector);
    const currentNode = currentParent.querySelector(selector);

    if (newNode && currentNode) {
      currentNode.replaceWith(newNode.cloneNode(true));
      return;
    }

    if (newNode && !currentNode) {
      const checkoutBlock = currentParent.querySelector('.cart-drawer__footer.cart-drawer__checkout');
      if (checkoutBlock) {
        checkoutBlock.insertAdjacentElement('afterend', newNode.cloneNode(true));
      }
      return;
    }

    currentNode?.remove();
  }

  updateCartDrawerFooter(doc) {
    const newFooter = doc.querySelector('.drawer__footer');
    const currentFooter = document.querySelector('#CartDrawer .drawer__footer');
    if (!newFooter || !currentFooter) return;

    const newCheckoutBlock = newFooter.querySelector('.cart-drawer__footer.cart-drawer__checkout');
    const currentCheckoutBlock = currentFooter.querySelector('.cart-drawer__footer.cart-drawer__checkout');

    const newTotalValue = newFooter.querySelector('.totals__total-value');
    const currentTotalValue = currentFooter.querySelector('.totals__total-value');
    if (newTotalValue && currentTotalValue) {
      currentTotalValue.innerHTML = newTotalValue.innerHTML;
    }

    const newDiscountsBlock = newFooter.querySelector('.cart-drawer__footer > div:first-child');
    const currentDiscountsBlock = currentFooter.querySelector('.cart-drawer__footer > div:first-child');
    if (newDiscountsBlock && currentDiscountsBlock) {
      currentDiscountsBlock.innerHTML = newDiscountsBlock.innerHTML;
    }

    if (newCheckoutBlock && currentCheckoutBlock) {
      const newSummary = newCheckoutBlock.querySelector('.cart-checkout-summary');
      const currentSummary = currentCheckoutBlock.querySelector('.cart-checkout-summary');
      if (newSummary && currentSummary) {
        currentSummary.innerHTML = newSummary.innerHTML;
      }

      this.syncFooterNode(currentCheckoutBlock, newCheckoutBlock, '.cart-customs-notice');
      this.syncFooterNode(currentCheckoutBlock, newCheckoutBlock, '.cart-rush-processing--offer');
      this.syncFooterNode(currentCheckoutBlock, newCheckoutBlock, '.cart-rush-processing--setup');
      this.syncFooterNode(currentCheckoutBlock, newCheckoutBlock, '.cart-rush-processing__status--inline');
    }

    const newTaxNote = newFooter.querySelector('.tax-note');
    const currentTaxNote = currentFooter.querySelector('.tax-note');
    if (newTaxNote && currentTaxNote) {
      currentTaxNote.innerHTML = newTaxNote.innerHTML;
    }

    const checkoutBtn = currentFooter.querySelector('#CartDrawer-Checkout');
    const newCheckoutBtn = newFooter.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn && newCheckoutBtn) {
      checkoutBtn.disabled = newCheckoutBtn.hasAttribute('disabled');
    }
  }

  updateCartDrawerProgress(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newProgress = doc.querySelector('.main_progressbar');
    const currentProgress = document.querySelector('cart-drawer .main_progressbar');
    if (!newProgress || !currentProgress) return;

    const fills = currentProgress.querySelectorAll('.progress-fill');
    fills.forEach((el) => {
      el.style.transition = 'none';
    });

    const newShippingFill = newProgress.querySelector('.main_progress_bar_left .progress-fill');
    const currentShippingFill = currentProgress.querySelector('.main_progress_bar_left .progress-fill');
    if (newShippingFill && currentShippingFill) {
      currentShippingFill.style.width = newShippingFill.style.width;
    }

    const newShippingMessage = newProgress.querySelector('.main_progress_bar_left .progress-message');
    const currentShippingMessage = currentProgress.querySelector('.main_progress_bar_left .progress-message');
    if (newShippingMessage && currentShippingMessage) {
      currentShippingMessage.className = newShippingMessage.className;
      currentShippingMessage.innerHTML = newShippingMessage.innerHTML;
    }

    const newEndAmount = newProgress.querySelector('.main_progress_bar_left .end-amount');
    const currentEndAmount = currentProgress.querySelector('.main_progress_bar_left .end-amount');
    if (newEndAmount && currentEndAmount) {
      currentEndAmount.textContent = newEndAmount.textContent;
    }

    const newHygiene = newProgress.querySelector('.hygiene-progress');
    const currentHygiene = currentProgress.querySelector('.hygiene-progress');

    if (newHygiene && currentHygiene) {
      const newHygieneFill = newHygiene.querySelector('.progress-fill--hygiene');
      const currentHygieneFill = currentHygiene.querySelector('.progress-fill--hygiene');
      if (newHygieneFill && currentHygieneFill) {
        currentHygieneFill.style.width = newHygieneFill.style.width;
      }

      const newHygieneMessage = newHygiene.querySelector('.hygiene-progress-message');
      const currentHygieneMessage = currentHygiene.querySelector('.hygiene-progress-message');
      if (newHygieneMessage && currentHygieneMessage) {
        currentHygieneMessage.className = newHygieneMessage.className;
        currentHygieneMessage.innerHTML = newHygieneMessage.innerHTML;
      }

      const newHygieneCount = newHygiene.querySelector('.hygiene-progress-count');
      const currentHygieneCount = currentHygiene.querySelector('.hygiene-progress-count');
      if (newHygieneCount && !currentHygieneCount) {
        currentHygiene.querySelector('.hygiene-progress-text')?.appendChild(newHygieneCount.cloneNode(true));
      } else if (!newHygieneCount && currentHygieneCount) {
        currentHygieneCount.remove();
      } else if (newHygieneCount && currentHygieneCount) {
        currentHygieneCount.textContent = newHygieneCount.textContent;
      }

      currentHygiene.dataset.promoCount = newHygiene.dataset.promoCount;
      currentHygiene.dataset.promoGoal = newHygiene.dataset.promoGoal;
      currentHygiene.dataset.promoApplied = newHygiene.dataset.promoApplied;
    } else if (newHygiene && !currentHygiene) {
      currentProgress.appendChild(newHygiene.cloneNode(true));
    } else if (!newHygiene && currentHygiene) {
      currentHygiene.remove();
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        currentProgress.querySelectorAll('.progress-fill').forEach((el) => {
          el.style.transition = '';
        });
      });
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
